/**
 * Das Urteil am Partieende, aus Sicht des Betrachters (`R13`).
 *
 * **Entschieden vom User am 2026-10-13** (nach Empfehlung): eine Karte über dem Brett, oben das
 * Urteil für den Betrachter, darunter Sieger, Stand und Grund. Diese Funktion rechnet, was darauf
 * steht; die Karte (`ResultCard`) zeigt es nur.
 *
 * **Wer gewonnen hat, rechnet die Engine** (`outcome`, §26) — hier wird nur verglichen, ob der
 * Betrachter auf der Seite des Siegers steht. Eine Seite ist ein Team oder, ohne Team, der
 * Spieler selbst (`sideOf`).
 */

import {
  sideOf,
  sidesOf,
  type GameState,
  type PlayerId,
  type VictoryReason,
} from '@flaghex/engine';

/**
 * - `won` / `lost` — für jemanden, der mitspielt
 * - `draw` — kein Sieger (Variante B, Zyklus R1)
 * - `over` — die Partie ist zu Ende, der Betrachter spielt nicht mit (ein Host ohne Platz)
 * - `aborted` — der Host hat beendet (`R12`): kein Ergebnis
 */
export type Verdict = 'won' | 'lost' | 'draw' | 'over' | 'aborted';

export interface SideScore {
  readonly side: string;
  readonly points: number;
}

export interface ResultView {
  readonly verdict: Verdict;
  /** Die Seite des Siegers; `null` bei Unentschieden, `undefined` bei einem Abbruch. */
  readonly winner: string | null | undefined;
  readonly reason: VictoryReason | undefined;
  /** Je Seite die Summe ihrer Punkte, die höchste zuerst. */
  readonly scores: readonly SideScore[];
}

function scoresOf(state: GameState): readonly SideScore[] {
  return sidesOf(state)
    .map((side) => ({
      side,
      points: state.players
        .filter((player) => sideOf(state, player.id) === side)
        .reduce((sum, player) => sum + (state.scores.get(player.id) ?? 0), 0),
    }))
    .sort((a, b) => b.points - a.points);
}

/** `undefined`, solange die Partie läuft. */
export function resultOf(state: GameState, viewer: PlayerId | undefined): ResultView | undefined {
  if (state.status === 'ABORTED') {
    return { verdict: 'aborted', winner: undefined, reason: undefined, scores: scoresOf(state) };
  }
  if (state.status !== 'FINISHED' || state.outcome === undefined) {
    return undefined;
  }
  const { winner, reason } = state.outcome;
  const plays = viewer !== undefined && state.players.some((player) => player.id === viewer);
  const verdict: Verdict =
    winner === null ? 'draw' : !plays ? 'over' : sideOf(state, viewer) === winner ? 'won' : 'lost';
  return { verdict, winner, reason, scores: scoresOf(state) };
}
